import { useContext } from 'react';
import { Box, Button, Grid, IconButton, InputAdornment, MenuItem, TextField } from '@mui/material';
import { ClearOutlined as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import { ordersContext } from '../OrdersContext';
import { FilterEditor, FilterSummary } from 'components/Common';
import { applicationContext } from 'helpers/services';
import moment from 'moment';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { FilterContainer } from 'components';
import ReferenceDataKey from 'helpers/enums/ReferenceDataKey';
import { ChannelType, OrderFilters } from 'helpers/enums';

let Filters = () => {
    let service = useContext(ordersContext);
    let appService = useContext(applicationContext);


    let getDatesLabel = () => {
        let label = "Order date";

        if (service.search.dateFrom) {
            label = `from: ${moment(service.search.dateFrom).format("LL")}`;
        }
        if (service.search.dateTo) {
            if (label.startsWith('from'))
                label = label + ` - To: ${moment(service.search.dateTo).format("LL")} `
            else
                label = `To: ${moment(service.search.dateTo).format("LL")} `
        }

        return label;
    }

    return (
        <FilterContainer>
            {service.filtersByQueue.getValueByKey(service.search.queue).indexOf(OrderFilters.OrderNumber) !== -1 &&
                <Grid item xs={2}>
                    <TextField
                        name="orderNumber"
                        fullWidth
                        size='small'
                        label="Search"
                        value={service.search.orderNumber}
                        onChange={(e: any) => service.updateSearch({
                            ...service.search,
                            orderNumber: e.target.value
                        })}
                    />
                </Grid>
            }

            {service.filtersByQueue.getValueByKey(service.search.queue).indexOf(OrderFilters.Status) !== -1 &&
                <Grid item>
                    <FilterSummary
                        attributeLabel='Order status'
                        color={service.search.status !== '' ? 'primary' : 'secondary'}
                        labelValue={appService.referenceData[ReferenceDataKey.OrderStatus].getValueByKey(service.search.status) ?? ''}
                        onClick={(e: any) => service.openEdit("status", e)}
                    />
                    {service.selectedFilter === "status" &&
                        <FilterEditor target={service.anchorEl} onClose={service.closeEdit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Order status"
                                        name="status"
                                        fullWidth
                                        margin="dense"
                                        size='small'
                                        select
                                        value={service.search.status}
                                        onChange={(e: any) => service.updateSearch({
                                            ...service.search,
                                            status: e.target.value
                                        })}
                                    >
                                        <MenuItem key={''} value={''}>Any</MenuItem>
                                        {appService.referenceData.OrderStatus.map((orderStatus) =>
                                            <MenuItem key={orderStatus.key} value={orderStatus.key}>{orderStatus.value}</MenuItem>
                                        )}
                                    </TextField>
                                </Grid>
                            </Grid>
                        </FilterEditor>
                    }
                </Grid>
            }

            {service.filtersByQueue.getValueByKey(service.search.queue).indexOf(OrderFilters.Channel) !== -1 &&
                <Grid item>
                    <FilterSummary
                        attributeLabel='Channel'
                        color={service.search.channel !== '' ? 'primary' : 'secondary'}
                        labelValue={appService.referenceData[ReferenceDataKey.Channel].getValueByKey(service.search.channel) ?? ''}
                        onClick={(e: any) => service.openEdit("channel", e)}
                    />
                    {service.selectedFilter === "channel" &&
                        <FilterEditor target={service.anchorEl} onClose={service.closeEdit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Channel"
                                        name="channel"
                                        fullWidth
                                        margin="dense"
                                        size='small'
                                        select
                                        value={service.search.channel}
                                        onChange={(e: any) => service.updateSearch({
                                            ...service.search,
                                            channel: e.target.value
                                        })}
                                    >
                                        <MenuItem key={''} value={''}>Any</MenuItem>
                                        {appService.referenceData.Channel.map((channel) =>
                                            <MenuItem key={channel.key} value={channel.key}>{channel.value}</MenuItem>
                                        )}
                                    </TextField>
                                </Grid>
                            </Grid>
                        </FilterEditor>
                    }
                </Grid>
            }

            {service.filtersByQueue.getValueByKey(service.search.queue).indexOf(OrderFilters.DateFrom) !== -1 &&
                <Grid item>
                    <FilterSummary
                        attributeLabel={getDatesLabel()}
                        color={(service.search.dateFrom || service.search.dateTo) ? 'primary' : 'secondary'}
                        onClick={(e: any) => service.openEdit("dates", e)}
                    />
                    {service.selectedFilter === "dates" &&
                        <FilterEditor target={service.anchorEl} onClose={service.closeEdit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <DateRangePicker
                                        value={[service.search.dateFrom, service.search.dateTo]}
                                        onChange={(newValue: DateRange<moment.Moment>) => {
                                            service.updateSearch({
                                                ...service.search,
                                                dateFrom: newValue && moment(newValue[0]),
                                                dateTo: newValue && moment(newValue[1])
                                            });
                                        }}
                                        renderInput={(startProps, endProps) => (
                                            <>
                                                <TextField
                                                    {...startProps}
                                                    variant="outlined"
                                                    size='small'
                                                    InputProps={{
                                                        sx: { paddingRight: 0.5 },
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    size='small'
                                                                    onClick={() =>
                                                                        service.updateSearch({
                                                                            ...service.search,
                                                                            dateFrom: null,
                                                                            dateTo: null
                                                                        })
                                                                    }
                                                                >
                                                                    <ClearIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <Box sx={{ ml: 1.5 }} />
                                                <TextField
                                                    {...endProps}
                                                    variant="outlined"
                                                    size='small'
                                                    InputProps={{
                                                        sx: { paddingRight: 0.5 },
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    size='small'
                                                                    onClick={() =>
                                                                        service.updateSearch({
                                                                            ...service.search,
                                                                            dateTo: null
                                                                        })
                                                                    }
                                                                >
                                                                    <ClearIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </>
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </FilterEditor>
                    }
                </Grid>
            }

            {service.filtersByQueue.getValueByKey(service.search.queue).indexOf(OrderFilters.Sku) !== -1 &&
                <Grid item>
                    <FilterSummary
                        attributeLabel='Sku'
                        color={service.search.sku !== '' ? 'primary' : 'secondary'}
                        labelValue={service.search.sku ?? ''}
                        onClick={(e: any) => service.openEdit("sku", e)}
                    />
                    {service.selectedFilter === "sku" &&
                        <FilterEditor target={service.anchorEl} onClose={service.closeEdit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Sku"
                                        name="sku"
                                        fullWidth
                                        margin="dense"
                                        size='small'
                                        value={service.search.sku}
                                        onChange={(e: any) => service.updateSearch({
                                            ...service.search,
                                            sku: e.target.value
                                        })}
                                    />
                                </Grid>
                            </Grid>
                        </FilterEditor>
                    }
                </Grid>
            }

            {service.filtersByQueue.getValueByKey(service.search.queue).indexOf(OrderFilters.ChannelType) !== -1 &&
                <Grid item>
                    <FilterSummary
                        attributeLabel='Channel type'
                        color={service.search.channelType !== '' ? 'primary' : 'secondary'}
                        labelValue={appService.referenceData[ReferenceDataKey.ChannelType].getValueByKey(service.search.channelType) ?? ''}
                        onClick={(e: any) => service.openEdit("channelType", e)}
                    />
                    {service.selectedFilter === "channelType" &&
                        <FilterEditor target={service.anchorEl} onClose={service.closeEdit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Channel type"
                                        name="ChannelType"
                                        fullWidth
                                        margin="dense"
                                        size='small'
                                        select
                                        value={service.search.channelType}
                                        onChange={(e: any) => service.updateSearch({
                                            ...service.search,
                                            channelType: e.target.value
                                        })}
                                    >
                                        <MenuItem key={''} value={''}>Any</MenuItem>
                                        {appService.referenceData.ChannelType.map((channelType) =>
                                            <MenuItem key={channelType.key} value={channelType.key}>{channelType.value}</MenuItem>
                                        )}
                                    </TextField>
                                </Grid>
                            </Grid>
                        </FilterEditor>
                    }
                </Grid>
            }

            <Grid item>
                <Button
                    type="submit"
                    variant="outlined"
                    onClick={() => service.doSearch(service.search)}
                    size="medium"
                    color="primary"
                >
                    <SearchIcon />
                </Button>
            </Grid>
        </FilterContainer>
    )
}

export default Filters;